import { useState } from 'react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useCustomValuesStore } from '../../../../stores/customValuesStore'
import { useNameStore } from '../../../../stores/nameStore'
import { copyToClipboard } from '@creanizer/utils'
import type { 
  NameType, 
  NameStyle, 
  NameMotif,
  NameSubcategory,
  GeneratedName,
  NameGeneratorInput
} from '@creanizer/types'
import type { CharacterInput, CharacterResult, SavedCharacter } from '../../../../types/character' // temp fix

export function useNameGenerator() {
  // 基本設定
  const [nameType, setNameType] = useState<NameType>('person_full')
  const [customNameType, setCustomNameType] = useState('')
  const [subcategory, setSubcategory] = useState<NameSubcategory | undefined>(undefined)
  const [customSubcategory, setCustomSubcategory] = useState('')
  const [style, setStyle] = useState<NameStyle>('modern_japanese')
  const [customStyle, setCustomStyle] = useState('')
  
  // エラー状態
  const [error, setError] = useState<string | null>(null)
  
  // 詳細設定
  const [rarity, setRarity] = useState(5)
  const [count, setCount] = useState<10 | 50 | 100>(10)
  const [motif, setMotif] = useState<NameMotif | ''>('')
  const [customMotif, setCustomMotif] = useState('')
  const [seed, setSeed] = useState('')
  const [autoSeed, setAutoSeed] = useState(true)
  const [usePro, setUsePro] = useState(false)
  const [useProLite, setUseProLite] = useState(false)
  const [useGemini3FlashMode, setUseGemini3FlashMode] = useState(false)
  const [useStreaming, setUseStreaming] = useState(true)
  
  // 世界観設定
  const [worldId, setWorldId] = useState<string | undefined>(undefined)
  const [worldContext, setWorldContext] = useState<string | undefined>(undefined)
  
  // 文字列制約機能
  const [includeString, setIncludeString] = useState<string>('')
  const [includePosition, setIncludePosition] = useState<'start' | 'end' | 'random' | 'given_name_start' | undefined>(undefined)
  const [fixedFamilyName, setFixedFamilyName] = useState<string>('')
  const [fixedGivenName, setFixedGivenName] = useState<string>('')
  const [avoidString, setAvoidString] = useState<string>('')
  const [minLength, setMinLength] = useState<number | undefined>(undefined)
  const [maxLength, setMaxLength] = useState<number | undefined>(undefined)
  const [rhymePattern, setRhymePattern] = useState<string>('')
  const [givenNameKanaType, setGivenNameKanaType] = useState<'katakana' | 'hiragana' | undefined>(undefined)
  
  // 結果
  const [results, setResults] = useState<GeneratedName[]>([])
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const [lastUsedSeed, setLastUsedSeed] = useState<number | null>(null)
  
  const { isApiKeySet, activeApiKey } = useSettingsStore()
  const generator = useGenerator('name')
  const streamGenerator = useStreamGenerator('name')
  const { 
    customValues, 
    addCustomValue, 
    getCustomValues, 
    useCustomValue 
  } = useCustomValuesStore()
  const { saveName, isNameSaved } = useNameStore()
  
  // ネームジェネレータ用の保存済みカスタム値を取得
  const savedStyles = getCustomValues('style', 'name')
  const savedMotifs = getCustomValues('motif', 'name')
  const savedSubcategories = getCustomValues('subcategory', 'name', nameType)
  const savedNameTypes = getCustomValues('nameType' as any, 'name')

  const handleGenerate = async () => {
    setError(null) // エラーをクリア
    setResults([]) // 結果をクリア
    
    try {
      // 自動シードの場合はランダム生成
      const seedValue = autoSeed 
        ? Math.floor(Math.random() * 2147483647)
        : seed ? parseInt(seed) : undefined

      // フロントエンドでもモード確認のログを出力
      if (useGemini3FlashMode) {
        console.log('[Frontend] Using Gemini 3 Flash Mode for Name Generation')
      } else if (usePro) {
        console.log('[Frontend] Using Pro Mode for Name Generation')
      } else if (useProLite) {
        console.log('[Frontend] Using Pro Lite Mode for Name Generation (thinking_budget: 1024)')
      } else {
        console.log('[Frontend] Using Standard Mode for Name Generation')
      }

      const input: NameGeneratorInput = {
        nameType: customNameType || nameType,
        subcategory: subcategory === 'custom' ? customSubcategory : subcategory,
        style: style === 'custom' ? customStyle : style,
        rarity,
        count,
        motif: motif === 'custom' ? customMotif : (motif || undefined),
        seed: seedValue,
        usePro,
        useProLite,
        useGemini3FlashMode,
        worldId,
        worldContext,
        includeString: includeString || undefined,
        includePosition: includePosition || undefined,
        fixedFamilyName: fixedFamilyName || undefined,
        fixedGivenName: fixedGivenName || undefined,
        avoidString: avoidString || undefined,
        minLength: minLength || undefined,
        maxLength: maxLength || undefined,
        rhymePattern: rhymePattern || undefined,
        givenNameKanaType: givenNameKanaType || undefined,
      }

      if (useStreaming) {
        await streamGenerator.generateStream(input, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              setResults(resultData as GeneratedName[])
              
              // 使用したシード値を保存
              if (seedValue) {
                setLastUsedSeed(seedValue)
                // 自動シードの場合、入力欄にも表示
                if (autoSeed) {
                  setSeed(seedValue.toString())
                }
              }
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(input)
            }
          }
        })
      } else {
        const result = await generator.mutateAsync(input)
        setResults(result.result as GeneratedName[])
        
        // 実際に使用されたモデルをログ出力
        if (result.modelUsed) {
          console.log('[Backend Confirmed] Actually used model:', result.modelUsed)
        }
        
        // 使用したシード値を保存
        if (seedValue) {
          setLastUsedSeed(seedValue)
          // 自動シードの場合、入力欄にも表示
          if (autoSeed) {
            setSeed(seedValue.toString())
          }
        }
      }
    } catch (error: any) {
      console.error('Generation failed:', error)
      
      // エラーメッセージを設定
      if (error.message?.includes('Empty response')) {
        setError('生成に失敗しました。もう一度お試しください。')
      } else if (error.message?.includes('rate limit')) {
        setError('APIの利用制限に達しました。しばらく待ってから再試行してください。')
      } else if (error.message?.includes('API key')) {
        setError('APIキーが設定されていないか、無効です。')
      } else if (error.message?.includes('parse')) {
        setError('生成結果の処理に失敗しました。もう一度お試しください。')
      } else {
        setError('エラーが発生しました: ' + (error.message || '不明なエラー'))
      }
    }
  }

  const handleCopy = async (name: string, index: number) => {
    const success = await copyToClipboard(name)
    if (success) {
      setCopiedIndex(index)
      setTimeout(() => setCopiedIndex(null), 2000)
    }
  }

  const handleSaveName = (name: GeneratedName) => {
    const input: NameGeneratorInput = {
      nameType: (customNameType || nameType) as NameType,
      subcategory: subcategory === 'custom' ? customSubcategory : subcategory,
      style: style === 'custom' ? customStyle : style,
      rarity,
      count,
      motif: motif === 'custom' ? customMotif : (motif || undefined),
      seed: lastUsedSeed || undefined,
      usePro,
      useProLite,
      useGemini3FlashMode,
      worldId,
      worldContext,
      includeString: includeString || undefined,
      includePosition: includePosition || undefined,
      fixedFamilyName: fixedFamilyName || undefined,
      fixedGivenName: fixedGivenName || undefined,
      avoidString: avoidString || undefined,
      minLength: minLength || undefined,
      maxLength: maxLength || undefined,
      rhymePattern: rhymePattern || undefined,
      givenNameKanaType: givenNameKanaType || undefined,
    }
    
    const id = saveName(name, input)
    if (id) {
      return true
    }
    return false
  }

  return {
    // State
    nameType,
    setNameType,
    customNameType,
    setCustomNameType,
    subcategory,
    setSubcategory,
    customSubcategory,
    setCustomSubcategory,
    style,
    setStyle,
    customStyle,
    setCustomStyle,
    rarity,
    setRarity,
    count,
    setCount,
    motif,
    setMotif,
    customMotif,
    setCustomMotif,
    seed,
    setSeed,
    autoSeed,
    setAutoSeed,
    usePro,
    setUsePro,
    useProLite,
    setUseProLite,
    useGemini3FlashMode,
    setUseGemini3FlashMode,
    useStreaming,
    setUseStreaming,
    worldId,
    setWorldId,
    worldContext,
    setWorldContext,
    includeString,
    setIncludeString,
    includePosition,
    setIncludePosition,
    fixedFamilyName,
    setFixedFamilyName,
    fixedGivenName,
    setFixedGivenName,
    avoidString,
    setAvoidString,
    minLength,
    setMinLength,
    maxLength,
    setMaxLength,
    rhymePattern,
    setRhymePattern,
    givenNameKanaType,
    setGivenNameKanaType,
    results,
    copiedIndex,
    lastUsedSeed,
    error,
    
    // Store values
    isApiKeySet,
    activeApiKey,
    generator,
    streamGenerator,
    savedStyles,
    savedMotifs,
    savedSubcategories,
    savedNameTypes,
    
    // Store functions
    addCustomValue,
    useCustomValue,
    saveName,
    isNameSaved,
    
    // Handlers
    handleGenerate,
    handleCopy,
    handleSaveName,
  }
}